/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { SectionListItem } from '@airasia-phoenix/react-native/components/SearchableListPageView';

/**
 * Filters and maps country data for searchable list
 * @param countries - Array of country data to filter and map
 * @param searchTerm - Search term to filter countries by name or dialing code
 * @returns Array of mapped country items for searchable list
 */

// Country data interface
export interface CountryData {
  cultureCode: string;
  countryCode3C: string;
  dialingCode: string;
  countryCode: string;
  name: string;
  defaultCurrencyCode: string;
}

export type SelectedCountry = {
  id: string;
  model: CountryData;
  primaryText: string;
  title: string;
};

function groupCountriesByAlphabet(
  countries: CountryData[],
  searchTerm: string,
): SectionListItem[] {
  const filteredCountries = filterAndMapCountries(countries, searchTerm);
  const grouped: Record<string, SelectedCountry[]> = {};

  filteredCountries.forEach((country) => {
    const firstLetter = country.model.name.charAt(0).toUpperCase();
    if (!grouped[firstLetter]) grouped[firstLetter] = [];
    grouped[firstLetter].push(country);
  });

  return Object.keys(grouped)
    .sort()
    .map((letter) => ({
      title: letter,
      data: grouped[letter],
    }));
}

function filterAndMapCountries(
  countries: CountryData[],
  searchTerm: string,
  includeDialingCode: boolean = true,
  searchCountryCode: boolean = false,
): {
  id: string;
  model: CountryData;
  primaryText: string;
  title: string;
}[] {
  return countries
    .filter((country: CountryData) => {
      const targetField = searchCountryCode ? 'countryCode' : 'name';
      const match =
        country[targetField]
          ?.toLowerCase()
          ?.includes(searchTerm.toLowerCase()) ||
        country.dialingCode?.includes(searchTerm.replace('+', ''));
      return match;
    })
    .map((country: CountryData) => {
      const displayText = includeDialingCode
        ? `${country.name} (+${country.dialingCode})`
        : country.name;
      return {
        id: country.countryCode,
        model: country,
        primaryText: displayText,
        title: displayText,
      };
    });
}

export { filterAndMapCountries, groupCountriesByAlphabet };
